import React, { useState, useMemo } from 'react';
import './style.scss';
import PropTypes from 'prop-types';
import DefaultChip from 'components/Utils/DefaultChip';
import {
  QuestionnaireBadge,
  STATUS,
  StudentConsentBadge,
} from 'scenes/HomeStaff/ClinicQueue/QueueBadge';
import Icon from 'components/Utils/NewIcon';
import AppointmentUtils from 'services/AppointmentUtils';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import moment from 'moment-timezone';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import RouteUtils from 'services/RouteUtils';
import { ActivePhysician, IsTypeNurse } from 'careand-redux/selectors';
import { v4 as uuidV4 } from 'uuid';
import PerformanceUtils from 'services/PerformanceUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import CPPUtils from 'services/CPPUtils';
import { WithConfirmation } from 'components/Utils/ConfirmationDialog/WithConfirmation';
import MiscUtils from 'services/MiscUtils';
import { useSafePersistedState } from 'hooks/usePersistedState';
import DefaultButton from 'components/Utils/NewDefaultButton';

const StartAppointmentNowChip = ({ patient, appointment, loadingAction }) => {
  const dispatch = useAppDispatch();
  const nav = useNavStack(Stacks.Master);
  const options = useAppSelector((state) => state.appointments.options);
  const physician = useAppSelector(ActivePhysician);
  const [formData, setFormData] = useSafePersistedState('StartAppointmentNowChip.formData.v2', {});
  const [confirmationDialog, showConfirmationDialog] = useState(undefined);
  const [confirmType, setConfirmType] = useState();
  const [confirmPayment, setConfirmPayment] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);

  const isNurse = useAppSelector(IsTypeNurse);
  if (!isNurse) return null;

  async function bookAppointment(
    event,
    data,
    clickedConfirmPayment = false,
    clickedSkipPayment = false,
  ) {
    const selection = data?.['Appointment'];
    const concern_id = selection?.concernId || pendingBooking?.concern_id;

    const option = options.find((o) => !!o.concerns?.find(({ id }) => id === concern_id));
    const concern = option?.concerns.find(({ id }) => id === concern_id);

    if (!concern) {
      return MiscUtils.showToastrError(
        'Book Appointment',
        'Please select an appointment type and concern',
      );
    }

    const deliveryOptions = concern.delivery_options || [];
    let chosenDelivery = selection?.deliveryOption || deliveryOptions[0];
    let deliveryOptionIndex = deliveryOptions.findIndex((o) => o === chosenDelivery);
    if (deliveryOptionIndex === -1) {
      deliveryOptionIndex = pendingBooking?.delivery_option_index ?? 0;
      chosenDelivery = deliveryOptions[deliveryOptionIndex];
    }
    let deliveryData = chosenDelivery
      ? { ...chosenDelivery, delivery_option_index: deliveryOptionIndex }
      : {};

    let appointment = {
      date: moment().unix(),
      concern_id: concern.id,
      clinic_id: physician.clinic_id,
      ...deliveryData,
      patient_id: patient.id,
      physician_id: physician.id,
      skip_notifications: true,
    };

    if (confirmPayment) clickedConfirmPayment = confirmPayment;

    setPendingBooking(appointment);

    const response = await dispatch(
      api.Appointments.bookAppointment({
        appointment,
        userId: appointment.patient_id,
        confirmPayment: clickedConfirmPayment,
        skipPayment: clickedSkipPayment,
        confirmSlot: true,
      }),
      {
        meta: true,
        title: 'Appointment Booking',
        successText: 'The appointment has been booked successfully!',
      },
    );

    if (AppointmentUtils.responseNeedsConfirmation(response.status)) {
      if (response.status === 402) setConfirmType('payment');
      else if (response.status === 409) setConfirmType('slot');
      if (response.error?.response?.jsonBodyMessage) {
        showConfirmationDialog(response.error.response.jsonBodyMessage);
      }
    } else if (!response.isSuccess) {
      // If request still fails after confirmation, reset options so they're asked again
      setConfirmPayment(false);
      setConfirmType(undefined);
      setPendingBooking(null);
    }

    if (response.isSuccess) {
      // Navigate to the bubble with the appointment ID
      if (response.result?.id) {
        let appointment = await dispatch(
          api.Appointments.updateAppointment({
            appointment: {
              ...response.result,
              status: AppointmentUtils.getAppointmentStatus().checked_in,
            },
          }),
        );

        setConfirmPayment(false);
        setConfirmType(undefined);
        setPendingBooking(null);

        const route = RouteUtils.getRoute(RouteUtils.routes().bubble, {
          ':bubbleId': appointment.id,
        });
        nav.push(route);
      }
    }
  }

  async function onConfirm() {
    if (confirmType === 'payment') return onConfirmPayment();
  }

  async function onSkipPayment() {
    setConfirmPayment(true);
    showConfirmationDialog(undefined);
    await bookAppointment(undefined, undefined, undefined, true);
  }

  async function onConfirmPayment() {
    setConfirmPayment(true);
    showConfirmationDialog(undefined);
    await bookAppointment(undefined, undefined, true);
  }

  const appointmentForm = useMemo(
    () => [
      {
        type: 'booking-concern',
        label: 'Appointment',
        patientId: patient.id,
        withDeliveryOptions: true,
        value: formData['Appointment'],
      },
    ],
    [formData, patient.id],
  );

  if (appointment) {
    return null;
  }

  return (
    <>
      {confirmationDialog && (
        <ConfirmationDialog
          open={!!confirmationDialog}
          message={confirmationDialog}
          onConfirm={onConfirm}
          onCancel={() => {
            showConfirmationDialog(undefined);
            setConfirmType(undefined);
            setConfirmPayment(false);
            setPendingBooking(null);
          }}
          additionalButton={
            confirmType === 'payment' && (
              <DefaultButton
                name="Book With No Payment"
                id="alert-dialog-skip"
                onClick={onSkipPayment}
                style={{ marginRight: '20px', width: '190px' }}
              />
            )
          }
        />
      )}
      <WithConfirmation
        message="Are you sure you want to start an appointment now?"
        onConfirm={bookAppointment}
        onInputValuesChanged={setFormData}
        form={appointmentForm}
        confirmDisabled={!formData['Appointment']?.concernId}
      >
        {(open) => (
          <DefaultChip
            text="Start Appointment Now"
            disabled={loadingAction}
            backgroundColor="#2693d1"
            textColor="white"
            onClick={open}
            avatar={<Icon fontAwesome="calendar-clock" className="white ml-1" height="15px" />}
          />
        )}
      </WithConfirmation>
    </>
  );
};

const TimeBar = ({
  patient,
  bubble,
  onArchiveChat,
  queueElement,
  showNavButtons = false,
  hidePanel = false,
  onToggleHidePanel,
  openList = false,
  onToggleOpenList,
  onScrollToTop,
}) => {
  const nav = useNavStack(Stacks.Master);

  const prescriptionFormId = CPPUtils.getFormName('prescription', patient.id);

  const [closeBubbleDialog, setCloseBubbleDialog] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [waitModal, setWaitModal] = useState(false);
  const queue = useAppSelector((state) => state.queue.elements);
  const prescriptionForm = useAppSelector((state) => state.rhfForms[prescriptionFormId]);

  const dispatch = useAppDispatch();

  const patientAppointments = useAppSelector((state) => state.appointments.byId[patient.id]);
  const physician = useAppSelector(ActivePhysician);

  const { default_room } = physician;

  const patientHasBubbles = !!bubble;

  const ownQueueElements = useMemo(() => {
    return queue.filter(({ physician_id }) => physician_id === physician.id);
  }, [physician, queue]);

  const { ownHasHelp, ownHasClean } = useMemo(() => {
    return {
      ownHasHelp: ownQueueElements.find(({ status }) => status === STATUS.help),
      ownHasClean: ownQueueElements.find(({ status }) => status === STATUS.clean),
    };
  }, [ownQueueElements]);

  function closeCPP() {
    let status = closeBubbleDialog;
    setCloseBubbleDialog(false);
    onArchiveChat(undefined, bubble?.appointment_id, status);
  }

  function openDialog(status) {
    setCloseBubbleDialog(status);
  }

  async function updatePhysician(physician) {
    await dispatch(api.Physicians.updatePhysician({ id: physician.id, physician }));
  }

  async function pinPatient() {
    let pinned_bubbles = PerformanceUtils.clone(physician.pinned_bubbles || []);

    pinned_bubbles.push({ patient_id: patient.id, notes: '' });

    await updatePhysician({ ...physician, pinned_bubbles });
    const route = RouteUtils.getRoute(RouteUtils.routes().bubble, {
      ':bubbleId': patient.id,
    });
    nav.push(route);
  }

  async function unpinPatient() {
    const pinned_bubbles = PerformanceUtils.clone(physician.pinned_bubbles || []).filter(
      (bubble) => bubble.patient_id !== patient.id,
    );
    await updatePhysician({ ...physician, pinned_bubbles });
    nav.push('/');
  }

  const appointmentId = queueElement?.appointment_id || bubble?.appointment_id;

  const { appointment, linkedAppointment } = useMemo(() => {
    let appointment = patientAppointments?.find(({ id }) => id === appointmentId);
    let linkedAppointment = patientAppointments?.find(({ id }) =>
      appointment?.linked?.find((it) => it.id === id),
    );
    return { appointment, linkedAppointment };
  }, [patientAppointments, appointmentId]);

  const isClinicAppointment = AppointmentUtils.isClinicAppointment(appointment);
  const isCalledIn = queueElement?.status === STATUS.called_in;
  const isWaiting = queueElement?.status === STATUS.waiting;
  const canCallHelp =
    !ownHasHelp && !!appointment && !AppointmentUtils.isTelemedicineAppointment(appointment);
  const canCallClean = !ownHasClean;
  const canCheckinSplit =
    linkedAppointment?.status === AppointmentUtils.getAppointmentStatus().scheduled && isCalledIn;

  async function changeQueue(changes, element = queueElement) {
    if (element) {
      const copy = {
        ...PerformanceUtils.clone(element),
        ...changes,
      };
      setLoadingAction(true);
      await dispatch(api.Queue.updateQueueElement({ id: copy.id, queueElement: copy }));
      setLoadingAction(false);
    } else {
      let queueElement = {
        id: uuidV4(),
        physician_id: physician.id,
        room: physician.default_room,
        clinic_id: physician.clinic_id,
        date: moment().unix(),
        patient_id: physician.id,
        ...changes,
      };
      await dispatch(api.Queue.addQueueElement({ queueElement }));
    }
  }

  async function cancelAssistance() {
    let queue = ownQueueElements.find(
      ({ status }) => status === STATUS.help || status === STATUS.clean,
    );
    return changeQueue({ status: STATUS.called_in }, queue);
  }

  async function requestAssistance() {
    let room = queueElement?.room || physician?.default_room || PhysicianUtils.getName(physician);
    return changeQueue({ status: STATUS.help, room });
  }

  async function checkinSplit() {
    if (linkedAppointment?.id) {
      await dispatch(
        api.Appointments.updateAppointmentStatus({
          id: linkedAppointment.id,
          status: 'checked_in',
        }),
        {
          errorTitle: 'Appointment',
        },
      );
    }
  }

  async function removeAndClean() {
    let room = queueElement?.room || physician?.default_room || PhysicianUtils.getName(physician);
    if (queueElement) {
      await removePatient();
    }
    let element = {
      id: uuidV4(),
      physician_id: physician.id,
      status: STATUS.clean,
      room,
      clinic_id: physician.clinic_id,
      date: moment().unix(),
      patient_id: physician.id,
    };
    await dispatch(api.Queue.addQueueElement({ queueElement: element }));
  }

  async function requestCleaning() {
    let room = queueElement?.room || physician?.default_room || PhysicianUtils.getName(physician);
    return changeQueue({ status: STATUS.clean, room });
  }

  async function callIn() {
    return changeQueue({
      status: STATUS.called_in,
      room: default_room,
      wait_until: undefined,
      wait_message: undefined,
    });
  }

  async function onWait(event, input) {
    let wait_message = input['Message'];
    let minutes = input['Wait minutes'];
    let wait_until = minutes ? parseInt(minutes) : undefined;
    if (wait_until) wait_until = moment().add(wait_until, 'minutes').unix();
    setWaitModal(false);
    return changeQueue({ status: STATUS.waiting, wait_until, wait_message });
  }

  async function removePatient() {
    setLoadingAction(true);
    if (queueElement) {
      await dispatch(api.Queue.deleteQueueElement({ id: queueElement.id }), {
        errorTitle: 'Queue Patient',
      });
    }
    setLoadingAction(false);
  }

  // Prefer the concern text (more informative than the appointment-type label
  // already conveyed by the contextual icon). Fall back to the bubble's free
  // description when no appointment is attached.
  const descriptionText = bubble?.appointment
    ? AppointmentUtils.getConcernText(bubble.appointment)
    : bubble?.description || '';

  // Tooltip body for the description chip — complaints + notes only (the
  // concern itself is already visible as the chip label).
  const descriptionTooltip = useMemo(() => {
    const appt = bubble?.appointment;
    if (!appt) return '';
    const parts = [];
    if (appt.complaints) {
      const label =
        appt.type === AppointmentUtils.getAppointmentTypes().blocked ? 'Reason' : 'Complaints';
      parts.push(`${label}: ${appt.complaints}`);
    }
    if (appt.notes) parts.push(`Notes: ${appt.notes}`);
    return parts.join('\n\n');
  }, [bubble?.appointment]);

  return (
    <div className="time-bar">
      {showNavButtons && (
        <div className="nav-buttons">
          <div className="nav-icon" onClick={onToggleHidePanel}>
            <Icon
              src={hidePanel ? 'chevron-right' : 'chevron-left'}
              alt={hidePanel ? 'Show Panel' : 'Hide Panel'}
              showTooltip={true}
              tooltipPlace="bottom"
              height="14px"
              width="14px"
            />
          </div>
          <div className={`nav-icon${openList ? ' active' : ''}`} onClick={onToggleOpenList}>
            <Icon
              src="bars"
              alt="See All Notes"
              showTooltip={true}
              tooltipPlace="bottom"
              width="14px"
              height="14px"
            />
          </div>
        </div>
      )}
      <div className="left">
        {waitModal && (
          <ConfirmationDialog
            open={waitModal}
            message="Do you want to set a message for the admin and a time limit for waiting?"
            onConfirm={onWait}
            onCancel={() => setWaitModal(false)}
            form={[
              { type: 'textField', label: 'Message' },
              { type: 'textField', label: 'Wait minutes' },
            ]}
          />
        )}
        {!bubble && !patientHasBubbles && (
          <DefaultChip
            text="Create Bubble"
            onClick={pinPatient}
            backgroundColor="var(--color-bg-page)"
            textColor="var(--color-action-primary)"
            avatar={<Icon fontAwesome="thought-bubble" className="white" height="15px" />}
          />
        )}
        {!!bubble?.manual && (
          <DefaultChip
            text="Close Bubble"
            backgroundColor="var(--color-bg-page)"
            textColor="var(--color-action-primary)"
            onClick={unpinPatient}
            avatar={<Icon fontAwesome="xmark" className="white" height="15px" />}
          />
        )}
        {!!bubble && !bubble.manual && (
          <DefaultChip
            text="Mark Completed"
            backgroundColor="var(--color-bg-page)"
            textColor="var(--color-action-primary)"
            onClick={() => openDialog('completed')}
          />
        )}
        {!!bubble?.appointment &&
          AppointmentUtils.isTelemedicineAppointment(bubble?.appointment) && (
            <DefaultChip
              text="Mark No-Show"
              backgroundColor="var(--color-status-danger)"
              textColor="var(--color-text-on-action)"
              onClick={() => openDialog('no_show')}
              avatar={<Icon fontAwesome="calendar-exclamation" className="white" height="15px" />}
            />
          )}
        {isClinicAppointment && isWaiting && default_room && (
          <DefaultChip
            text={`Call In to ${default_room}`}
            backgroundColor="#2693d1"
            textColor="white"
            disabled={loadingAction}
            onClick={callIn}
            avatar={<Icon fontAwesome="door-open" className="white" height="15px" />}
          />
        )}
        {isCalledIn && (
          <>
            <DefaultChip
              text="Remove from Queue"
              backgroundColor="var(--color-bg-page)"
              textColor="var(--color-action-primary)"
              disabled={loadingAction}
              onClick={removePatient}
              avatar={
                <Icon fontAwesome="trash" className="var(--color-action-primary)" height="15px" />
              }
            />
            {isClinicAppointment && (
              <DefaultChip
                text="Wait"
                backgroundColor="var(--color-bg-page)"
                textColor="var(--color-action-primary)"
                disabled={loadingAction}
                onClick={() => setWaitModal(true)}
                avatar={
                  <Icon
                    fontAwesome="hourglass"
                    className="var(--color-action-primary)"
                    height="15px"
                  />
                }
              />
            )}
          </>
        )}
        {closeBubbleDialog && (
          <CloseDialog
            bubble={bubble}
            closeBubbleDialog={closeBubbleDialog}
            setCloseBubbleDialog={setCloseBubbleDialog}
            closeCPP={closeCPP}
            patient={patient}
            appointment={appointment}
            prescriptionForm={prescriptionForm}
          />
        )}
        {canCallHelp && (
          <DefaultChip
            text="Request Assistance"
            backgroundColor="var(--color-status-danger)"
            textColor="var(--color-text-on-action)"
            disabled={loadingAction}
            onClick={requestAssistance}
            avatar={<Icon fontAwesome="triangle-exclamation" className="white" height="15px" />}
          />
        )}
        {canCallClean && (
          <>
            <DefaultChip
              text="Clean"
              backgroundColor="var(--color-status-warning)"
              textColor="var(--color-text-on-action)"
              disabled={loadingAction}
              onClick={requestCleaning}
              avatar={<Icon fontAwesome="broom" className="white" height="15px" />}
            />
            {queueElement && (
              <DefaultChip
                text="Remove & Clean"
                backgroundColor="var(--color-status-warning)"
                textColor="var(--color-text-on-action)"
                disabled={loadingAction}
                onClick={removeAndClean}
                avatar={
                  <>
                    <Icon fontAwesome="trash" className="white ml-1" height="15px" />
                    <Icon fontAwesome="broom" className="white" height="15px" />
                  </>
                }
              />
            )}
          </>
        )}
        {(ownHasHelp || ownHasClean) && (
          <DefaultChip
            text={`Cancel ${ownHasHelp ? 'Assistance' : 'Cleaning'} Request`}
            backgroundColor={ownHasHelp ? '#FF3B5E' : '#FFAE03'}
            textColor="white"
            disabled={loadingAction}
            onClick={cancelAssistance}
            avatar={<Icon fontAwesome="xmark" className="white" height="15px" />}
          />
        )}
        {canCheckinSplit && (
          <DefaultChip
            text="Check-in Next Appointment"
            disabled={loadingAction}
            onClick={checkinSplit}
            backgroundColor="#2693d1"
            textColor="white"
            avatar={<Icon fontAwesome="door-open" className="white ml-1" height="15px" />}
          />
        )}
        <StartAppointmentNowChip
          patient={patient}
          appointment={appointment}
          loadingAction={loadingAction}
        />
      </div>
      {!!bubble?.appointment?.questionnaire && (
        <QuestionnaireBadge
          appointment={bubble?.appointment}
          questionnaire={bubble?.appointment?.questionnaire}
        />
      )}
      {(queueElement || !!bubble?.appointment?.questionnaire) && (
        <StudentConsentBadge
          questionnaire={bubble?.appointment?.questionnaire}
          element={queueElement}
        />
      )}
      {!!descriptionText && (
        <DefaultChip
          className="description"
          text={descriptionText}
          alt={descriptionTooltip || undefined}
          backgroundColor="var(--color-bg-muted)"
          textColor="var(--color-text-secondary)"
          avatar={
            <Icon
              src={
                bubble?.appointment
                  ? AppointmentUtils.getIcon(bubble.appointment)
                  : 'calendar-clock'
              }
              height="15px"
            />
          }
          element={
            !!descriptionTooltip && (
              <Icon src={['fas', 'memo']} height="15px" className="description-memo" />
            )
          }
        />
      )}
      {showNavButtons && (
        <div className="nav-icon nav-icon--end" onClick={onScrollToTop}>
          <Icon
            src="arrow-up-to-line"
            alt="Scroll to Top"
            showTooltip={true}
            tooltipPlace="bottom"
            width="14px"
            height="14px"
          />
        </div>
      )}
    </div>
  );
};

const CloseDialog = ({
  bubble,
  closeBubbleDialog,
  setCloseBubbleDialog,
  closeCPP,
  patient,
  appointment,
  prescriptionForm,
}) => {
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);
  const drafts = useAppSelector(
    (state) => state.healthRecords.byId[patient.id]?.appointmentNoteDrafts,
  );
  const faxes = useAppSelector((state) => state.fax.patients[patient.id]) || [];
  const todaysRXFaxes = faxes.filter(
    (fax) => fax.title === 'Prescription' && fax.date > appointment?.date,
  );
  const hasPendingDrafts = drafts?.find((it) => it.author_id === activeUserId);

  // Check for unsaved prescription form
  const hasUnsavedMedications = prescriptionForm?.medications?.length > 0;

  // Check for saved prescriptions from this appointment
  const prescriptions = useAppSelector(
    (state) => state.healthRecords.byId[patient.id]?.prescriptions || [],
  );

  const appointmentPrescriptions = appointment?.date
    ? prescriptions.filter((rx) => rx.date >= appointment.date)
    : [];

  const awaitingApproval = appointmentPrescriptions.filter((rx) => rx.waiting_approval);

  const savedButUnsent = appointmentPrescriptions.length - todaysRXFaxes.length;

  let message;

  if (closeBubbleDialog === 'completed') {
    let warnings = [];

    if (hasUnsavedMedications) {
      warnings.push('You have unsaved prescription(s) in the form.');
    }

    if (awaitingApproval.length > 0) {
      warnings.push(
        `You have ${awaitingApproval.length} prescription(s) awaiting approval from supervisor.`,
      );
    }

    if (savedButUnsent.length > 0) {
      warnings.push(
        `You have ${savedButUnsent.length} prescription(s) that haven't been sent to pharmacy or printed.`,
      );
    }

    if (hasPendingDrafts) {
      warnings.push('You still have not saved some of your appointment notes.');
    }

    if (warnings.length > 0) {
      message = warnings.join(' ') + ' Are you sure you want to mark the appointment completed?';
    } else if (!!closeBubbleDialog && bubble && bubble.appointment_id) {
      let status = AppointmentUtils.getAppointmentStatus()[closeBubbleDialog];
      message = `This will mark the appointment as "${status}". Proceed?`;
    } else {
      message = 'This will archive the chat. Proceed?';
    }
  } else if (!!closeBubbleDialog && bubble && bubble.appointment_id) {
    let status = AppointmentUtils.getAppointmentStatus()[closeBubbleDialog];
    message = `This will mark the appointment as "${status}". Proceed?`;
  } else {
    message = 'This will archive the chat. Proceed?';
  }

  function onClose() {
    setCloseBubbleDialog(false);
  }

  return (
    <ConfirmationDialog
      title="Confirm close"
      message={message}
      open={closeBubbleDialog}
      onConfirm={closeCPP}
      onCancel={onClose}
    />
  );
};

TimeBar.propTypes = {
  bubble: PropTypes.object,
  onArchiveChat: PropTypes.func,
  showNavButtons: PropTypes.bool,
  hidePanel: PropTypes.bool,
  onToggleHidePanel: PropTypes.func,
  openList: PropTypes.bool,
  onToggleOpenList: PropTypes.func,
  onScrollToTop: PropTypes.func,
};

export default TimeBar;
